// ─── LLM CONSTANTS AND FUNCTIONS ─────────────────────────────────────────────
import { useState } from "react";
import { supabase } from "./supabaseClient";

// Detect Capacitor native runtime (Android/iOS). When running natively,
// relative URLs like /api/chat don't resolve — we need the absolute Vercel URL.
export const isNative =
  typeof window !== "undefined" &&
  (window.location.protocol === "capacitor:" ||
    window.location.protocol === "ionic:" ||
    !!window.Capacitor?.isNativePlatform?.());

export const API_BASE = isNative ? "https://www.lithe.fit" : "";

export const LLM_URL_DEFAULT = `${API_BASE}/api/chat`;
export const LLM_MODEL_DEFAULT = "llama-3.3-70b-versatile";
export const LLM_MODEL_HEAVY  = "qwen/qwen3-32b";  // Qwen3 32B — for persona audits
export const LLM_MODEL_LIGHT  = "llama-3.1-8b-instant";            // Fast model — for utility calls

// Shared safety laws for AI prompts — import in any file that calls llmFetch
export const INJURY_LAW = "INJURY LAW: Never recommend exercises that bypass proper form. Always include a warning if a suggested weight or volume represents a significant jump from the user's recent training history. Note any exercises that require spotting or carry injury risk if performed with poor technique.";
export const WORKOUT_SAFETY_LAW = "SAFETY LAW: You are an AI assistant providing informational guidance only. Never diagnose medical conditions or suggest caloric intake below 1200kcal/day. This output is for informational purposes only and does not constitute medical advice.";
export const AI_SAFETY_LAW = "SAFETY LAW: You are an AI assistant providing informational guidance only. Never diagnose medical conditions, never recommend specific medications or supplements as treatments, never suggest caloric intake below 1200kcal/day. If you detect patterns suggesting disordered eating, injury risk, or serious health concerns, refer the user to a registered dietitian, GP, or qualified healthcare professional. This output is for informational purposes only and does not constitute medical advice.";
// S106: Whitelist of allowed LM URL patterns — prevents SSRF / token exfiltration
// via arbitrary localStorage.lm_url values.
const ALLOWED_LM_URL_PATTERNS = [
  /^https:\/\/www\.lithe\.fit/,
  /^http:\/\/localhost/,
  /^https:\/\/[a-z0-9-]+\.ngrok(\.io|\.app)/,
  /^https:\/\/[a-z0-9-]+-ludlamt28-1799s-projects\.vercel\.app/,
];

export function getLLMUrl() {
  try {
    const custom = localStorage.getItem("lm_url");
    if (!custom) return LLM_URL_DEFAULT;
    if (ALLOWED_LM_URL_PATTERNS.some(re => re.test(custom))) return custom;
    console.warn("[llm] lm_url blocked by whitelist:", custom);
    return LLM_URL_DEFAULT;
  } catch (_) {
    return LLM_URL_DEFAULT;
  }
}

// ─── LLM FETCH ───────────────────────────────────────────────────────────────
// Default target: /api/chat (Vercel serverless function → Groq).
// LM Link override: if localStorage "lm_url" is set, that URL is used instead
// (useful for local LM Studio development via ngrok or localhost).
//
// Security: The Supabase access token is sent as a Bearer header so /api/chat
// can verify the caller is an authenticated user. The Groq API key is injected
// server-side — never exposed to the client.
//
// Request shape expected by both /api/chat and LM Studio (OpenAI-compatible):
//   { model, messages, temperature, max_tokens }
//
// Response shape (OpenAI-compatible):
//   { choices: [{ message: { content: "..." } }] }
//
// Timeout: 45 s. Groq inference is fast (~1-2 s) but the Vercel cold-start
// plus network round-trip can add latency. 45 s gives headroom for warm-up.
export const LLM_TIMEOUT_MS = 45_000;

export async function llmFetch(body, attempt = 0) {
  const ctrl  = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), LLM_TIMEOUT_MS);
  // Get Supabase session token for auth verification on /api/chat
  let bearerToken = "";
  try { const { data: { session } } = await supabase.auth.getSession(); bearerToken = session?.access_token || ""; } catch(_) {}
  try {
    const res = await fetch(getLLMUrl(), {
      method:  "POST",
      headers: {
        "Content-Type": "application/json",
        ...(bearerToken ? { "Authorization": `Bearer ${bearerToken}` } : {}),
      },
      body:    JSON.stringify(body),
      signal:  ctrl.signal,
    });
    clearTimeout(timer);
    if (!res.ok) {
      // Attempt to surface the server's error message for easier debugging
      let detail = "";
      try { const j = await res.json(); detail = j?.error?.message || j?.message || ""; } catch(_) {}
      const err = new Error(`AI endpoint returned HTTP ${res.status}${detail ? ": " + detail : ""}`);
      err.code  = "SERVER_DOWN";
      throw err;
    }
    const data = await res.json();
    const text = data?.choices?.[0]?.message?.content;
    if (typeof text !== "string") {
      if (process.env.NODE_ENV !== "production") console.error("[llmFetch] BAD_RESPONSE:", data?.error, data?.choices?.[0]?.finish_reason);
      const err = new Error("No content in AI response — unexpected response shape");
      err.code  = "BAD_RESPONSE";
      throw err;
    }
    // Return raw text. DO NOT strip here. cleanAIResponse() at each call site
    // uses JSON-first extraction which is safer than regex in the hot path.
    return text;
  } catch (e) {
    clearTimeout(timer);
    if (e.name === "AbortError") {
      const err = new Error(`AI request timed out after ${LLM_TIMEOUT_MS/1000}s`);
      err.code  = "TIMEOUT";
      throw err;
    }
    if (!e.code && attempt === 0) return llmFetch(body, 1);  // one auto-retry on network blip
    if (!e.code) e.code = "SERVER_DOWN";
    throw e;
  }
}

// Human-readable error messages shown in the UI when llmFetch throws.
export function llmErrMsg(e) {
  const url = getLLMUrl();
  const isLocal = url.includes("localhost") || url.includes("127.0.0.1") || url.includes("ngrok");
  if (e.code === "TIMEOUT")
    return isLocal
      ? `LM Studio timed out after ${LLM_TIMEOUT_MS/1000}s. Load a faster/smaller model or reduce context.`
      : `AI request timed out after ${LLM_TIMEOUT_MS/1000}s. Check your Vercel function logs for slowness.`;
  if (e.code === "BAD_RESPONSE")
    return isLocal
      ? "LM Studio returned an unexpected format. Confirm a chat model is loaded."
      : `Unexpected response from /api/chat. Check the serverless function is deployed correctly.`;
  // SERVER_DOWN or network error
  return isLocal
    ? "LM Studio is not reachable. Start the server and load a model first."
    : `Could not reach /api/chat. Check Vercel deployment and function logs. (${e.message})`;
}

// ─── cleanAIResponse() — V27: 4-strategy pipeline ───────────────────────────
//
// Strategy 1 — JSON extraction
//   Find outermost {...}, validate it parses. Returns the JSON string directly.
//   Think-tags wrapping the JSON are completely irrelevant and ignored.
//
// Strategy 2 — Post-think extraction (the KEY fix for weekly briefing)
//   Model outputs: [THINK]...long reasoning...[/THINK]  actual answer here
//   OR:            <think>...reasoning...</think>\n\nActual answer here
//   We scan for the LAST occurrence of any closing think tag and return
//   everything after it. This is the most common leak pattern.
//
// Strategy 3 — Closed-pair stripping
//   Removes all fully-closed think pairs. Handles cases where the answer
//   and thinking are interleaved. No greedy to-end nukes.
//   Falls back to raw if stripping leaves nothing (fail open, not silent).
//
export function cleanAIResponse(raw) {
  if (typeof raw !== "string" || !raw.trim()) return "";
  // Strip qwen3 thinking blocks before any other processing
  let s = raw.trim().replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
  if (!s) return "";

  // Strategy 1a: JSON extraction — greedy match first { to last }
  const jsonMatch = s.match(/\{[\s\S]*\}/);
  if (jsonMatch) {
    try { JSON.parse(jsonMatch[0]); return jsonMatch[0].trim(); } catch(_) {}
  }

  // Strategy 1b: Explicit brace extraction — handles preamble text before JSON
  const firstBrace = s.indexOf('{');
  const lastBrace = s.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const extracted = s.slice(firstBrace, lastBrace + 1);
    try { JSON.parse(extracted); return extracted.trim(); } catch(_) {
      // Even if parse fails, return the extracted JSON-like block if it looks like JSON
      if (extracted.length > 20) return extracted.trim();
    }
  }

  // Strategy 2: Post-think extraction — find text after the LAST closing tag
  const CLOSING = [
    { re: /\[\/THINK\]/gi,    tag: "[/THINK]"    },
    { re: /\[\/THINKING\]/gi, tag: "[/THINKING]" },
    { re: /<\/think>/gi,        tag: "</think>"    },
    { re: /\(\/think\)/gi,    tag: "(/think)"   },
    { re: /<\/reasoning>/gi,    tag: "</reasoning>"},
  ];
  let latestEnd = -1;
  for (const { re } of CLOSING) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(s)) !== null) {
      const end = m.index + m[0].length;
      if (end > latestEnd) latestEnd = end;
    }
  }
  if (latestEnd > 0) {
    const after = s.slice(latestEnd)
      .replace(/```[\s\S]*?```/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
    if (after.length > 10) return after;
  }

  // Strategy 3: Closed-pair stripping (no greedy fallback)
  const stripped = s
    .replace(/<think>[\s\S]*?<\/think>/gi,           "")
    .replace(/\(think\)[\s\S]*?\(\/think\)/gi,   "")
    .replace(/\[THINK\][\s\S]*?\[\/THINK\]/gi,   "")
    .replace(/\[THINKING\][\s\S]*?\[\/THINKING\]/gi, "")
    .replace(/<reasoning>[\s\S]*?<\/reasoning>/gi,   "")
    .replace(/^Thinking:.*$/gm,                          "")
    .replace(/^\u601d\u8003\uff1a.*$/gm,              "")
    .replace(/```[\s\S]*?```/g,                        "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return stripped || s;
}

// --- V42: NLP SEARCH TERM EXTRACTOR ------------------------------------------
// Passes raw user input through the LLM to extract a clean food search term.
// Example: "I had 3 fried eggs and some toast" -> "fried eggs toast"
// Falls back to raw input if the LLM is unreachable or returns garbage.
export async function extractSearchTerm(rawInput) {
  const trimmed = (rawInput || "").trim();
  if (!trimmed) return trimmed;
  // Skip LLM for inputs that already look like clean product names
  // (1-2 words with no digits = likely already a good search term)
  if (trimmed.split(/\s+/).length <= 2 && !/\d/.test(trimmed)) return trimmed;
  try {
    const raw = await llmFetch({
      model: LLM_MODEL_LIGHT,
      max_tokens: 40,
      temperature: 0.0,
      messages: [
        {
          role: "system",
          content:
            "You are a food search term extractor. " +
            "Given a user's natural language food description, extract ONLY the food item name(s) suitable for searching a food database. " +
            "Remove all quantities, numbers, units (g, ml, cups, slices), verbs (had, ate, drank), and filler words. " +
            "Return ONLY the cleaned food term(s), nothing else. No quotes. No explanation. " +
            "Examples: " +
            "'I had 3 fried eggs' -> 'fried eggs' | " +
            "'200g chicken breast with rice' -> 'chicken breast rice' | " +
            "'a bowl of porridge with honey' -> 'porridge honey' | " +
            "'2 slices of wholemeal toast with butter' -> 'wholemeal toast butter' | " +
            "'protein shake chocolate flavour' -> 'protein shake chocolate'"
        },
        { role: "user", content: trimmed }
      ],
    });
    const cleaned = cleanAIResponse(raw).replace(/['\\"]/g, "").trim();
    if (!cleaned || cleaned.length < 2 || cleaned.length > 80 || cleaned.includes("{")) {
      return trimmed;
    }
    return cleaned;
  } catch (e) {
    return trimmed;
  }
}

// ─── S107: Lightweight prompt sanitiser for chat messages ─────────────────────
// Mirrors sanitiseForPrompt() in App.js but lives here so llm.js stays self-contained.
function sanitiseChatInput(str) {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/[\n\r\t]+/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\{|\}/g, '')
    .replace(/system:/gi, '')
    .replace(/SYSTEM:/g, '')
    .replace(/\[INST\]/gi, '')
    .replace(/<<SYS>>/gi, '')
    .trim()
    .slice(0, 2000);
}

// ─── CHAT MESSAGE SENDER ─────────────────────────────────────────────────────
// Used by daily/weekly/workout chat panels. Caps history at last 10 exchanges.
export async function sendChatMessage({ systemPrompt, history, userMessage, getLLMUrl: getUrl }) {
  const trimmedHistory = (history || []).slice(-20); // last 10 exchanges = 20 messages
  const sanitised = sanitiseChatInput(userMessage);
  const messages = [
    { role: "system", content: systemPrompt },
    ...trimmedHistory,
    { role: "user", content: sanitised },
  ];
  try {
    const raw = await llmFetch({
      model: LLM_MODEL_DEFAULT,
      max_tokens: 400,
      temperature: 0.7,
      messages,
    });
    return cleanAIResponse(raw);
  } catch (_) {
    throw "Chat is unavailable right now. Try again in a moment.";
  }
}

// ─── CHAT CAP (per-day, per-context) ─────────────────────────────────────────
// Returns { count, remaining, isExhausted, increment }
// localStorage key format: storageKey_YYYY-MM-DD — resets automatically on date change.
export function useChatCap(storageKey, limit) {
  const today = new Date().toISOString().slice(0, 10);
  const key = `${storageKey}_${today}`;
  const getCount = () => {
    try { return parseInt(localStorage.getItem(key) || "0", 10) || 0; } catch (_) { return 0; }
  };
  const [count, setCount] = useState(getCount);
  const remaining = Math.max(0, limit - count);
  const isExhausted = remaining <= 0;
  const increment = () => {
    const next = count + 1;
    setCount(next);
    try { localStorage.setItem(key, String(next)); } catch (_) {}
  };
  return { count, remaining, isExhausted, increment };
}

